import _slicedToArray from "C:\\Users\\Kristaps\\projects\\proj261\\site\\node_modules\\@babel\\runtime\\helpers\\esm\\slicedToArray.js";
var _excluded = ["id", "className", "containerClassName", "placeholder", "autoFocus", "textField", "dataKey", "autoSelectMatches", "focusFirstItem", "value", "defaultValue", "onChange", "open", "defaultOpen", "onToggle", "filter", "busy", "disabled", "readOnly", "selectIcon", "hideCaret", "hideEmptyPopup", "busySpinner", "dropUp", "tabIndex", "popupTransition", "name", "onSelect", "onKeyDown", "onBlur", "onFocus", "inputProps", "listProps", "groupBy", "renderListItem", "renderListGroup", "optionComponent", "listComponent", "popupComponent", "data", "messages"];

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

import cn from 'classnames';
import * as React from 'react';
import { useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useUncontrolledProp } from 'uncontrollable';
import { caretDown } from './Icon';
import Input from './Input';
import List from './List';
import { FocusListContext, useFocusList } from './FocusListContext';
import BasePopup from './Popup';
import InputAddon from './InputAddon';
import Widget from './Widget';
import WidgetPicker from './WidgetPicker';
import { useMessagesWithDefaults } from './messages';
import { useActiveDescendant } from './A11y';
import * as CustomPropTypes from './PropTypes';
import { useAccessors } from './Accessors';
import { useFilteredData } from './Filter';
import useDropdownToggle from './useDropdownToggle';
import useFocusManager from './useFocusManager';
import { notify, useFirstFocusedRender, useInstanceId } from './WidgetHelpers';
import { Spinner } from './Icon';

function indexOf(data, searchTerm, text) {
  if (!searchTerm.trim()) return -1;

  for (var idx = 0; idx < data.length; idx++) {
    if (text(data[idx]).toLowerCase() === searchTerm) return idx;
  }

  return -1;
}

/**
 * ---
 * shortcuts:
 *   - { key: alt + down arrow, label: open combobox }
 *   - { key: alt + up arrow, label: close combobox }
 *   - { key: down arrow, label: move focus to next item }
 *   - { key: up arrow, label: move focus to previous item }
 *   - { key: home, label: move focus to first item }
 *   - { key: end, label: move focus to last item }
 *   - { key: enter, label: select focused item }
 *   - { key: any key, label: search list for item starting with key }
 * ---
 *
 * Select an item from the list, or input a custom value. The Combobox can also make suggestions as you type.

 * @public
 */
var ComboboxImpl = /*#__PURE__*/React.forwardRef(function Combobox(_ref, outerRef) {
  var id = _ref.id,
      className = _ref.className,
      containerClassName = _ref.containerClassName,
      placeholder = _ref.placeholder,
      autoFocus = _ref.autoFocus,
      textField = _ref.textField,
      dataKey = _ref.dataKey,
      autoSelectMatches = _ref.autoSelectMatches,
      _ref$focusFirstItem = _ref.focusFirstItem,
      focusFirstItem = _ref$focusFirstItem === void 0 ? false : _ref$focusFirstItem,
      value = _ref.value,
      _ref$defaultValue = _ref.defaultValue,
      defaultValue = _ref$defaultValue === void 0 ? '' : _ref$defaultValue,
      onChange = _ref.onChange,
      open = _ref.open,
      _ref$defaultOpen = _ref.defaultOpen,
      defaultOpen = _ref$defaultOpen === void 0 ? false : _ref$defaultOpen,
      onToggle = _ref.onToggle,
      _ref$filter = _ref.filter,
      filter = _ref$filter === void 0 ? true : _ref$filter,
      busy = _ref.busy,
      disabled = _ref.disabled,
      readOnly = _ref.readOnly,
      _ref$selectIcon = _ref.selectIcon,
      selectIcon = _ref$selectIcon === void 0 ? caretDown : _ref$selectIcon,
      hideCaret = _ref.hideCaret,
      hideEmptyPopup = _ref.hideEmptyPopup,
      busySpinner = _ref.busySpinner,
      dropUp = _ref.dropUp,
      tabIndex = _ref.tabIndex,
      popupTransition = _ref.popupTransition,
      name = _ref.name,
      onSelect = _ref.onSelect,
      onKeyDown = _ref.onKeyDown,
      onBlur = _ref.onBlur,
      onFocus = _ref.onFocus,
      inputProps = _ref.inputProps,
      listProps = _ref.listProps,
      groupBy = _ref.groupBy,
      renderListItem = _ref.renderListItem,
      renderListGroup = _ref.renderListGroup,
      optionComponent = _ref.optionComponent,
      _ref$listComponent = _ref.listComponent,
      ListComponent = _ref$listComponent === void 0 ? List : _ref$listComponent,
      _ref$popupComponent = _ref.popupComponent,
      Popup = _ref$popupComponent === void 0 ? BasePopup : _ref$popupComponent,
      _ref$data = _ref.data,
      rawData = _ref$data === void 0 ? [] : _ref$data,
      userMessages = _ref.messages,
      elementProps = _objectWithoutPropertiesLoose(_ref, _excluded);

  var _useUncontrolledProp = useUncontrolledProp(value, defaultValue, onChange),
      _useUncontrolledProp2 = _slicedToArray(_useUncontrolledProp, 2),
      currentValue = _useUncontrolledProp2[0],
      handleChange = _useUncontrolledProp2[1];

  var _useUncontrolledProp3 = useUncontrolledProp(open, defaultOpen, onToggle),
      _useUncontrolledProp4 = _slicedToArray(_useUncontrolledProp3, 2),
      currentOpen = _useUncontrolledProp4[0],
      handleOpen = _useUncontrolledProp4[1];

  var ref = useRef(null);
  var inputRef = useRef(null);
  var listRef = useRef(null);

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      suggestion = _useState2[0],
      setSuggestion = _useState2[1];

  var shouldFilter = useRef(false);
  var inputId = useInstanceId(id, '_input');
  var listId = useInstanceId(id, '_listbox');
  var activeId = useInstanceId(id, '_listbox_active_option');
  var accessors = useAccessors(textField, dataKey);
  var messages = useMessagesWithDefaults(userMessages);
  var toggle = useDropdownToggle(currentOpen, handleOpen);
  var isDisabled = disabled === true;
  var isReadOnly = !!readOnly;
  var data = useFilteredData(rawData, filter, shouldFilter.current ? accessors.text(currentValue) : void 0, accessors.text);
  var selectedItem = useMemo(function () {
    return data[accessors.indexOf(data, currentValue)];
  }, [data, currentValue, accessors]);
  var list = useFocusList({
    activeId: activeId,
    scope: ref,
    focusFirstItem: focusFirstItem,
    anchorItem: currentOpen ? selectedItem : undefined
  });

  var _useFocusManager = useFocusManager(ref, {
    disabled: isDisabled,
    onBlur: onBlur,
    onFocus: onFocus
  }, {
    didHandle: function didHandle(focused) {
      if (!focused) {
        shouldFilter.current = false;
        toggle.close();
        setSuggestion(null);
        list.focus(undefined);
      } else {
        focus({
          preventScroll: true
        });
      }
    }
  }),
      _useFocusManager2 = _slicedToArray(_useFocusManager, 2),
      focusEvents = _useFocusManager2[0],
      focused = _useFocusManager2[1];

  useActiveDescendant(ref, activeId, currentOpen, [list.getFocused()]);
  /**
   * Handlers
   */

  var handleClick = function handleClick(e) {
    if (readOnly || isDisabled) return; // prevents double clicks when in a <label>

    e.preventDefault();
    focus();
    toggle();
  };

  var handleSelect = function handleSelect(data, originalEvent) {
    toggle.close();
    shouldFilter.current = false;
    setSuggestion(null);
    notify(onSelect, [data, {
      originalEvent: originalEvent
    }]);
    change(data, originalEvent, true);
    focus({
      preventScroll: true
    });
  };

  var handleInputKeyDown = function handleInputKeyDown(_ref2) {
    var key = _ref2.key;

    if (key === 'Backspace' || key === 'Delete') {
      list.focus(null);
    }
  };

  var handleInputChange = function handleInputChange(event) {
    var idx = autoSelectMatches ? indexOf(rawData, event.target.value.toLowerCase(), accessors.text) : -1;
    shouldFilter.current = true;
    setSuggestion(null);
    var nextValue = idx === -1 ? event.target.value : rawData[idx];
    change(nextValue, event);
    if (!nextValue) toggle.close();else toggle.open();
  };

  var handleKeyDown = function handleKeyDown(e) {
    if (readOnly) return;
    var key = e.key,
        altKey = e.altKey,
        shiftKey = e.shiftKey;
    notify(onKeyDown, [e]);
    if (e.defaultPrevented) return;

    var select = function select(item) {
      return item != null && handleSelect(item, e);
    };

    var setFocused = function setFocused(el) {
      if (!el) return;
      setSuggestion(list.toDataItem(el));
      list.focus(el);
    };

    if (key === 'End' && currentOpen && !shiftKey) {
      e.preventDefault();
      setFocused(list.last());
    } else if (key === 'Home' && currentOpen && !shiftKey) {
      e.preventDefault();
      setFocused(list.first());
    } else if (key === 'Escape' && currentOpen) {
      e.preventDefault();
      setSuggestion(null);
      toggle.close();
    } else if (key === 'Enter' && currentOpen) {
      e.preventDefault();
      select(list.getFocused());
    } else if (key === 'ArrowDown') {
      e.preventDefault();

      if (currentOpen) {
        setFocused(list.next());
      } else {
        return toggle.open();
      }
    } else if (key === 'ArrowUp') {
      e.preventDefault();
      if (altKey) return toggle.close();

      if (currentOpen) {
        setFocused(list.prev());
      }
    }
  };
  /**
   * Methods
   */


  function focus(opts) {
    if (inputRef.current) inputRef.current.focus(opts);
  }

  function change(nextValue, originalEvent) {
    var selected = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    handleChange(nextValue, {
      lastValue: currentValue,
      originalEvent: originalEvent,
      source: selected ? 'listbox' : 'input'
    });
  }
  /**
   * Rendering
   */


  useImperativeHandle(outerRef, function () {
    return {
      focus: focus
    };
  });
  var shouldRenderPopup = useFirstFocusedRender(focused, currentOpen);
  var valueItem = accessors.findOrSelf(data, currentValue);
  var inputValue = accessors.text(suggestion || valueItem);
  var completeType = filter ? 'list' : 'none';
  var popupOpen = currentOpen && (!hideEmptyPopup || !!data.length);
  var inputReadOnly = // @ts-ignore
  (inputProps == null ? void 0 : inputProps.readOnly) != null ? inputProps == null ? void 0 : inputProps.readOnly : readOnly;
  var inputAddon = false;

  if (!hideCaret) {
    inputAddon = /*#__PURE__*/React.createElement(InputAddon, {
      busy: busy,
      icon: selectIcon,
      spinner: busySpinner,
      onClick: handleClick,
      disabled: !!isDisabled || isReadOnly // FIXME
      ,
      label: messages.openCombobox()
    });
  } else if (busy) {
    inputAddon = /*#__PURE__*/React.createElement("span", {
      "aria-hidden": "true",
      className: "rw-btn rw-picker-caret"
    }, busySpinner || Spinner);
  }

  return /*#__PURE__*/React.createElement(Widget, _extends({}, elementProps, {
    ref: ref,
    open: currentOpen,
    dropUp: dropUp,
    focused: focused,
    disabled: isDisabled,
    readOnly: isReadOnly
  }, focusEvents, {
    onKeyDown: handleKeyDown,
    className: cn(className, 'rw-combobox')
  }), /*#__PURE__*/React.createElement(WidgetPicker, {
    className: cn(containerClassName, hideCaret && 'rw-widget-input', hideCaret && !busy && 'rw-hide-caret')
  }, /*#__PURE__*/React.createElement(Input, _extends({}, inputProps, {
    role: "combobox",
    name: name,
    id: inputId,
    className: cn( // @ts-ignore
    inputProps && inputProps.className, 'rw-combobox-input', !hideCaret && 'rw-widget-input'),
    autoFocus: autoFocus,
    tabIndex: tabIndex,
    disabled: isDisabled,
    readOnly: inputReadOnly,
    "aria-busy": !!busy,
    "aria-owns": listId,
    "aria-autocomplete": completeType,
    "aria-expanded": currentOpen,
    "aria-haspopup": true,
    placeholder: placeholder,
    value: inputValue,
    onChange: handleInputChange,
    onKeyDown: handleInputKeyDown,
    ref: inputRef
  })), inputAddon), /*#__PURE__*/React.createElement(FocusListContext.Provider, {
    value: list.context
  }, shouldRenderPopup && /*#__PURE__*/React.createElement(Popup, {
    dropUp: dropUp,
    open: popupOpen,
    transition: popupTransition,
    onEntering: function onEntering() {
      return listRef.current.scrollIntoView();
    }
  }, /*#__PURE__*/React.createElement(ListComponent, _extends({}, listProps, {
    id: listId,
    tabIndex: -1,
    data: data,
    groupBy: groupBy,
    disabled: disabled,
    accessors: accessors,
    renderItem: renderListItem,
    renderGroup: renderListGroup,
    optionComponent: optionComponent,
    value: selectedItem,
    searchTerm: valueItem && accessors.text(valueItem) || '',
    "aria-hidden": !popupOpen,
    "aria-labelledby": inputId,
    "aria-live": popupOpen ? 'polite' : void 0,
    onChange: function onChange(d, meta) {
      return handleSelect(d, meta.originalEvent);
    },
    ref: listRef,
    messages: {
      emptyList: rawData.length ? messages.emptyFilter : messages.emptyList
    }
  })))));
});
ComboboxImpl.displayName = 'Combobox';
export default ComboboxImpl;